import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { 
  HandCoins, Calculator, FileText, CheckCircle, XCircle, Clock,
  RefreshCw, Search, DollarSign, Calendar, User, AlertTriangle,
  ChevronRight, TrendingDown, Percent, CreditCard
} from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

export default function DnaPayRenegociacao() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('nova');
  const [renegociacoes, setRenegociacoes] = useState([]);
  const [limites, setLimites] = useState(null);
  
  // Estados para nova renegociação
  const [searchTerm, setSearchTerm] = useState('');
  const [titulosSelecionados, setTitulosSelecionados] = useState([]);
  const [titulosDisponiveis, setTitulosDisponiveis] = useState([]);
  const [responsavelBusca, setResponsavelBusca] = useState('');
  const [dividasPendentes, setDividasPendentes] = useState(null);
  
  // Estados da simulação
  const [simulacao, setSimulacao] = useState(null);
  const [simulando, setSimulando] = useState(false);
  const [configSimulacao, setConfigSimulacao] = useState({
    tipo: 'parcelamento',
    numero_parcelas: 3,
    desconto_percentual: 0,
    dia_vencimento: 10
  });
  
  // Estados do dialog
  const [showConfirmar, setShowConfirmar] = useState(false);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [selectedRenegociacao, setSelectedRenegociacao] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [criando, setCriando] = useState(false);

  const tiposRenegociacao = [
    { value: 'parcelamento', label: 'Parcelamento', desc: 'Divide a dívida em parcelas mensais' },
    { value: 'desconto_quitacao', label: 'Desconto para Quitação', desc: 'Desconto especial para pagamento à vista' },
    { value: 'reducao_encargos', label: 'Redução de Encargos', desc: 'Reduz multa e juros aplicados' },
    { value: 'prorrogacao', label: 'Prorrogação', desc: 'Estende o prazo de pagamento' }
  ];

  const statusColors = {
    proposta: 'bg-gray-100 text-gray-800',
    aguardando_aprovacao: 'bg-yellow-100 text-yellow-800',
    aprovada: 'bg-blue-100 text-blue-800',
    ativa: 'bg-green-100 text-green-800',
    quitada: 'bg-emerald-100 text-emerald-800',
    rejeitada: 'bg-red-100 text-red-800',
    inadimplente: 'bg-orange-100 text-orange-800',
    cancelada: 'bg-gray-100 text-gray-800'
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [renegRes, limitesRes] = await Promise.all([
        api.get('/dnapay/renegociacao/?limit=50'),
        api.get('/dnapay/renegociacao/limites')
      ]);
      
      setRenegociacoes(renegRes.data?.renegociacoes || []);
      setLimites(limitesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const buscarDividasResponsavel = async () => {
    if (!responsavelBusca) {
      toast.error('Informe o ID do responsável');
      return;
    }
    
    try {
      const res = await api.get(`/dnapay/renegociacao/responsavel/${responsavelBusca}/pendentes`);
      setDividasPendentes(res.data);
      setTitulosDisponiveis(res.data?.titulos || []);
      setTitulosSelecionados([]);
      setSimulacao(null);
    } catch (error) {
      toast.error('Erro ao buscar dívidas');
    }
  };

  const simularRenegociacao = async () => {
    if (titulosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um título');
      return;
    }
    
    setSimulando(true);
    try {
      const res = await api.post('/dnapay/renegociacao/simular', {
        titulo_ids: titulosSelecionados,
        tipo: configSimulacao.tipo,
        numero_parcelas: configSimulacao.numero_parcelas,
        desconto_percentual: configSimulacao.desconto_percentual,
        dia_vencimento: configSimulacao.dia_vencimento
      });
      
      setSimulacao(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao simular');
    } finally {
      setSimulando(false);
    }
  };

  const criarRenegociacao = async () => {
    setCriando(true);
    try {
      await api.post('/dnapay/renegociacao/criar', null, {
        params: {
          titulo_ids: titulosSelecionados,
          tipo: configSimulacao.tipo,
          numero_parcelas: configSimulacao.numero_parcelas,
          desconto_percentual: configSimulacao.desconto_percentual,
          dia_vencimento: configSimulacao.dia_vencimento,
          motivo,
          observacoes
        }
      });
      
      toast.success('Renegociação criada com sucesso!');
      setShowConfirmar(false);
      setSimulacao(null);
      setTitulosSelecionados([]);
      setMotivo('');
      setObservacoes('');
      loadData();
      setActiveTab('lista');
    } catch (error) {
      toast.error('Erro ao criar renegociação');
    } finally {
      setCriando(false);
    }
  };

  const aprovarRenegociacao = async (id) => {
    try {
      await api.post(`/dnapay/renegociacao/${id}/aprovar`);
      toast.success('Renegociação aprovada!');
      loadData();
      setShowDetalhes(false);
    } catch (error) {
      toast.error('Erro ao aprovar');
    }
  };

  const rejeitarRenegociacao = async (id, motivo) => {
    try {
      await api.post(`/dnapay/renegociacao/${id}/rejeitar`, null, {
        params: { motivo }
      });
      toast.success('Renegociação rejeitada');
      loadData();
      setShowDetalhes(false);
    } catch (error) {
      toast.error('Erro ao rejeitar');
    }
  };

  const verDetalhes = async (id) => {
    try {
      const res = await api.get(`/dnapay/renegociacao/${id}`);
      setSelectedRenegociacao(res.data);
      setShowDetalhes(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const toggleTitulo = (tituloId) => {
    setTitulosSelecionados(prev => 
      prev.includes(tituloId) 
        ? prev.filter(id => id !== tituloId)
        : [...prev, tituloId]
    );
    setSimulacao(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dnapay-renegociacao">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HandCoins className="w-6 h-6" />
            Renegociação de Dívidas
          </h1>
          <p className="text-muted-foreground">Parcelamentos e acordos financeiros</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Limites Info */}
      {limites && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Limites: Máximo <strong>{limites.max_parcelas}x</strong> | 
            Parcela mínima: <strong>{formatCurrency(limites.min_valor_parcela)}</strong> | 
            Desconto máximo principal: <strong>{limites.max_desconto_principal}%</strong>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="nova" data-testid="tab-nova">
            <Calculator className="w-4 h-4 mr-2" />
            Nova Renegociação
          </TabsTrigger>
          <TabsTrigger value="lista" data-testid="tab-lista">
            <FileText className="w-4 h-4 mr-2" />
            Renegociações ({renegociacoes.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab Nova Renegociação */}
        <TabsContent value="nova" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Buscar Dívidas do Responsável</CardTitle>
              <CardDescription>Informe o ID do responsável para buscar títulos pendentes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  placeholder="ID do responsável financeiro"
                  value={responsavelBusca}
                  onChange={(e) => setResponsavelBusca(e.target.value)}
                />
                <Button onClick={buscarDividasResponsavel}>
                  <Search className="w-4 h-4 mr-2" />
                  Buscar
                </Button>
              </div>
            </CardContent>
          </Card>

          {dividasPendentes && (
            <>
              {/* Resumo */}
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-orange-600">Títulos Pendentes</p>
                      <p className="text-2xl font-bold">{dividasPendentes.resumo?.quantidade || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-orange-600">Valor Original</p>
                      <p className="text-2xl font-bold">{formatCurrency(dividasPendentes.resumo?.valor_original || 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-orange-600">Multa + Juros</p>
                      <p className="text-2xl font-bold text-red-600">
                        {formatCurrency((dividasPendentes.resumo?.valor_multa || 0) + (dividasPendentes.resumo?.valor_juros || 0))}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-orange-600">Total da Dívida</p>
                      <p className="text-2xl font-bold">{formatCurrency(dividasPendentes.resumo?.valor_total || 0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Lista de Títulos */}
              <Card>
                <CardHeader>
                  <CardTitle>Selecione os Títulos para Renegociar</CardTitle>
                  <CardDescription>
                    {titulosSelecionados.length} título(s) selecionado(s)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12"></TableHead>
                          <TableHead>Descrição</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead>Valor Original</TableHead>
                          <TableHead>Encargos</TableHead>
                          <TableHead>Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {titulosDisponiveis.map((titulo) => (
                          <TableRow key={titulo.id}>
                            <TableCell>
                              <Checkbox
                                checked={titulosSelecionados.includes(titulo.id)}
                                onCheckedChange={() => toggleTitulo(titulo.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{titulo.descricao}</p>
                                <p className="text-xs text-muted-foreground">{titulo.aluno_nome}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {titulo.vencimento ? (() => { const m = String(titulo.vencimento).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : titulo.vencimento; })() : '-'}
                            </TableCell>
                            <TableCell>{formatCurrency(titulo.valor_final || 0)}</TableCell>
                            <TableCell className="text-red-600">
                              {formatCurrency((titulo.multa || 0) + (titulo.juros || 0))}
                            </TableCell>
                            <TableCell className="font-bold">
                              {formatCurrency(titulo.saldo_atualizado || titulo.valor_final || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Configuração da Simulação */}
              {titulosSelecionados.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Configurar Renegociação</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Tipo de Renegociação</Label>
                        <Select
                          value={configSimulacao.tipo}
                          onValueChange={(v) => setConfigSimulacao({ ...configSimulacao, tipo: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {tiposRenegociacao.map((tipo) => (
                              <SelectItem key={tipo.value} value={tipo.value}>
                                <div>
                                  <p>{tipo.label}</p>
                                  <p className="text-xs text-muted-foreground">{tipo.desc}</p>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Número de Parcelas</Label>
                        <Select
                          value={String(configSimulacao.numero_parcelas)}
                          onValueChange={(v) => setConfigSimulacao({ ...configSimulacao, numero_parcelas: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                              <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Desconto em Encargos: {configSimulacao.desconto_percentual}%</Label>
                        <Slider
                          value={[configSimulacao.desconto_percentual]}
                          onValueChange={([v]) => setConfigSimulacao({ ...configSimulacao, desconto_percentual: v })}
                          max={100}
                          step={5}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label>Dia de Vencimento</Label>
                        <Select
                          value={String(configSimulacao.dia_vencimento)}
                          onValueChange={(v) => setConfigSimulacao({ ...configSimulacao, dia_vencimento: parseInt(v) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[5, 10, 15, 20, 25].map((d) => (
                              <SelectItem key={d} value={String(d)}>Dia {d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <Button onClick={simularRenegociacao} disabled={simulando} className="w-full">
                      {simulando ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Calculator className="w-4 h-4 mr-2" />
                      )}
                      Simular Renegociação
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Resultado da Simulação */}
              {simulacao && (
                <Card className="border-green-200 bg-green-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <TrendingDown className="w-5 h-5" />
                      Resultado da Simulação
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">Dívida Total</p>
                        <p className="text-xl font-bold line-through text-red-500">
                          {formatCurrency(simulacao.valor_total_divida)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Desconto</p>
                        <p className="text-xl font-bold text-green-600">
                          -{formatCurrency(simulacao.desconto_aplicado)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Valor Final</p>
                        <p className="text-xl font-bold text-green-700">
                          {formatCurrency(simulacao.valor_final)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Parcelas</p>
                        <p className="text-xl font-bold">
                          {simulacao.numero_parcelas}x de {formatCurrency(simulacao.valor_parcela)}
                        </p>
                      </div>
                    </div>

                    <Alert className="bg-green-100 border-green-300">
                      <Percent className="h-4 w-4" />
                      <AlertDescription>
                        Economia de <strong>{formatCurrency(simulacao.economia)}</strong> ({simulacao.economia_percentual}% de desconto)
                      </AlertDescription>
                    </Alert>

                    {/* Parcelas */}
                    <div>
                      <p className="font-medium mb-2">Parcelas:</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {simulacao.parcelas?.map((p) => (
                          <div key={p.numero} className="p-2 bg-white rounded border text-center">
                            <p className="text-xs text-muted-foreground">Parcela {p.numero}</p>
                            <p className="font-bold">{formatCurrency(p.valor)}</p>
                            <p className="text-xs">{(() => { const m = String(p.vencimento).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : p.vencimento; })()}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button onClick={() => setShowConfirmar(true)} className="w-full bg-green-600 hover:bg-green-700">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Criar Proposta de Renegociação
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Tab Lista */}
        <TabsContent value="lista">
          <Card>
            <CardHeader>
              <CardTitle>Renegociações</CardTitle>
              <CardDescription>Propostas e acordos de renegociação</CardDescription>
            </CardHeader>
            <CardContent>
              {renegociacoes.length === 0 ? (
                <Alert>
                  <AlertDescription>Nenhuma renegociação encontrada.</AlertDescription>
                </Alert>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Responsável</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Valor Original</TableHead>
                      <TableHead>Valor Final</TableHead>
                      <TableHead>Parcelas</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {renegociacoes.map((renego) => (
                      <TableRow key={renego.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{renego.responsavel_nome}</p>
                            {renego.aluno_nome && (
                              <p className="text-xs text-muted-foreground">{renego.aluno_nome}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {tiposRenegociacao.find(t => t.value === renego.tipo)?.label || renego.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(renego.valor_total_divida)}</TableCell>
                        <TableCell className="font-bold">{formatCurrency(renego.valor_final)}</TableCell>
                        <TableCell>{renego.numero_parcelas}x {formatCurrency(renego.valor_parcela)}</TableCell>
                        <TableCell>
                          <Badge className={statusColors[renego.status]}>
                            {renego.status?.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {renego.created_at ? new Date(renego.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '-'}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => verDetalhes(renego.id)}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Confirmar Criação */}
      <Dialog open={showConfirmar} onOpenChange={setShowConfirmar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Renegociação</DialogTitle>
            <DialogDescription>
              A proposta será enviada para aprovação
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Motivo da Renegociação</Label>
              <Textarea
                placeholder="Descreva o motivo..."
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea
                placeholder="Observações adicionais..."
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmar(false)}>
              Cancelar
            </Button>
            <Button onClick={criarRenegociacao} disabled={criando}>
              {criando ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Criar Proposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalhes */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes da Renegociação</DialogTitle>
          </DialogHeader>

          {selectedRenegociacao && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Responsável</p>
                  <p className="font-medium">{selectedRenegociacao.renegociacao?.responsavel_nome}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={statusColors[selectedRenegociacao.renegociacao?.status]}>
                    {selectedRenegociacao.renegociacao?.status?.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Original</p>
                  <p className="font-bold">{formatCurrency(selectedRenegociacao.renegociacao?.valor_total_divida)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Final</p>
                  <p className="font-bold text-green-600">{formatCurrency(selectedRenegociacao.renegociacao?.valor_final)}</p>
                </div>
              </div>

              {/* Parcelas */}
              {selectedRenegociacao.parcelas?.length > 0 && (
                <div>
                  <p className="font-medium mb-2">Parcelas:</p>
                  <ScrollArea className="h-[200px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Parcela</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Vencimento</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedRenegociacao.parcelas.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell>{p.numero_parcela}</TableCell>
                            <TableCell>{formatCurrency(p.valor)}</TableCell>
                            <TableCell>{(() => { const m = String(p.vencimento).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : p.vencimento; })()}</TableCell>
                            <TableCell>
                              <Badge variant={p.status === 'pago' ? 'default' : 'secondary'}>
                                {p.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}

              {/* Ações */}
              {selectedRenegociacao.renegociacao?.status === 'aguardando_aprovacao' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => aprovarRenegociacao(selectedRenegociacao.renegociacao.id)}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Aprovar
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => rejeitarRenegociacao(selectedRenegociacao.renegociacao.id, 'Rejeitado pelo gestor')}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rejeitar
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
